/**
 * EnumPreloader
 *
 * Warms the in-memory enum cache for the UI namespaces that drive first-paint
 * chrome (nav menus, footer, search taxonomy) before rendering the app, so the
 * menu is populated immediately instead of after an async fetch.
 *
 * Resilient: renders children once the prefetch resolves OR after a short
 * timeout, whichever comes first — a slow or unreachable enum API never blocks
 * the app from loading.
 */

import React, { useEffect, useState } from 'react';
import { prefetchEnumOptions, prefetchEnumTree } from '../hooks/useEnum';

// UI enums needed for first-paint chrome.
const UI_TREE_NAMESPACES = [
  'nav.home_links',       // logged-out mega-menu + footer
  'nav.role_menu',        // logged-in role-based nav
  'property.search_types', // search taxonomy tree
];
const UI_OPTION_NAMESPACES = [
  'property.subtypes',    // flat subtype labels
];

// Max time to wait before rendering anyway (ms).
const MAX_WAIT_MS = 2500;

export default function EnumPreloader({ children }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let done = false;
    const finish = () => { if (!done) { done = true; setReady(true); } };

    // Safety timeout — never block the app on enum loading.
    const timer = setTimeout(finish, MAX_WAIT_MS);

    Promise.allSettled([
      ...UI_TREE_NAMESPACES.map((ns) => prefetchEnumTree(ns)),
      ...UI_OPTION_NAMESPACES.map((ns) => prefetchEnumOptions(ns)),
    ]).finally(() => {
      clearTimeout(timer);
      finish();
    });

    return () => clearTimeout(timer);
  }, []);

  if (!ready) {
    return (
      <div
        className="min-h-svh"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}
      >
        loading...
      </div>
    );
  }

  return children;
}
